import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from "react-native";

import type { FormDefinition } from "@/lib/api";
import { initiateVisitCall } from "@/lib/api";
import { useNotification } from "@/lib/notifications";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";
import { useWebSocketEvent } from "@/lib/websocket";

interface PatientContactsProps {
    visitId: string;
    patient: NonNullable<FormDefinition["patient"]>;
    visit: FormDefinition["visit"];
    /** false on aide-evaluation visits, where the caregiver is the aide and
     *  already has her own AideCard above this list. Defaults to true. */
    includeCaregiver?: boolean;
}

interface ContactEntry {
    name: string;
    label: string;
    contactRef: string;
    isPrimary: boolean;
    source: "patient" | "caregiver" | "emergency";
    callable: boolean;
}

function buildContactList(
    patient: PatientContactsProps["patient"],
    visit: PatientContactsProps["visit"],
    includeCaregiver: boolean
): ContactEntry[] {
    const contacts: ContactEntry[] = [];

    for (const pn of patient.phoneNumbers ?? []) {
        contacts.push({
            name: `${patient.firstName} ${patient.lastName}`,
            label: pn.label ?? "Phone",
            contactRef: pn.contactRef,
            isPrimary: pn.isPrimary ?? false,
            source: "patient",
            callable: typeof pn.contactRef === "string" && pn.contactRef.length > 0
        });
    }

    if (includeCaregiver && visit.caregiverContactRef) {
        contacts.push({
            name: visit.caregiverName ?? "Caregiver",
            label: "Caregiver",
            contactRef: visit.caregiverContactRef,
            isPrimary: false,
            source: "caregiver",
            callable: true
        });
    }
    if (includeCaregiver) {
        for (const cp of visit.caregiverPhones ?? []) {
            contacts.push({
                name: visit.caregiverName ?? "Caregiver",
                label: cp.label ?? "Caregiver",
                contactRef: cp.contactRef,
                isPrimary: cp.isPrimary ?? false,
                source: "caregiver",
                callable: typeof cp.contactRef === "string" && cp.contactRef.length > 0
            });
        }
    }

    for (const ec of patient.emergencyContacts ?? []) {
        contacts.push({
            name: ec.name ?? "Emergency Contact",
            label: ec.relationship ?? "Emergency",
            contactRef: ec.contactRef,
            isPrimary: false,
            source: "emergency",
            callable: Boolean(ec.hasPhone && ec.contactRef)
        });
    }

    return contacts;
}

export function PatientContacts({
    visitId,
    patient,
    visit,
    includeCaregiver = true
}: PatientContactsProps) {
    const contacts = buildContactList(patient, visit, includeCaregiver);
    const { showNotification } = useNotification();
    const [pendingRef, setPendingRef] = useState<string | null>(null);
    const [activeCallId, setActiveCallId] = useState<number | null>(null);

    useWebSocketEvent("call:updated", (data) => {
        if (activeCallId !== null && data.callId !== activeCallId) {
            return;
        }
        switch (data.action) {
            case "contact_answered":
                showNotification(
                    data.contactName ? `Connected to ${data.contactName}` : "Connected",
                    "approved"
                );
                setActiveCallId(null);
                break;
            case "no_answer":
                showNotification(
                    data.contactName ? `${data.contactName} didn't answer` : "No answer",
                    "info"
                );
                setActiveCallId(null);
                break;
            case "failed":
                showNotification("Call failed — try again", "failed");
                setActiveCallId(null);
                break;
            // dialing_rn / rn_answered / completed: no toast (optimistic
            // toast is already on screen and natural completion is silent)
        }
    });

    async function handleCall(contact: ContactEntry) {
        if (!contact.callable || pendingRef) {
            return;
        }
        if (!contact.contactRef) {
            showNotification(
                "This contact isn't callable yet — try again after the next deploy.",
                "failed"
            );
            return;
        }
        setPendingRef(contact.contactRef);
        showNotification(
            `You'll get a call shortly — answer to be connected to ${contact.name}.`,
            "info"
        );
        try {
            const result = await initiateVisitCall(visitId, contact.contactRef);
            setActiveCallId(result.callId);
        } catch (err) {
            const message = err instanceof Error ? err.message : "Couldn't start the call";
            showNotification(message, "failed");
        } finally {
            setPendingRef(null);
        }
    }

    if (contacts.length === 0) {
        return (
            <View style={styles.card}>
                <Text style={styles.sectionLabel}>CONTACTS</Text>
                <Text style={styles.emptyText}>No contacts available</Text>
            </View>
        );
    }

    return (
        <View style={styles.card}>
            <Text style={styles.sectionLabel}>CONTACTS</Text>
            {contacts.map((contact, i) => {
                const isPending = pendingRef === contact.contactRef;
                return (
                    <View
                        key={`${contact.contactRef}-${i}`}
                        style={[styles.row, i < contacts.length - 1 && styles.rowBorder]}
                    >
                        <View style={styles.contactInfo}>
                            <View style={styles.nameRow}>
                                <Text style={styles.contactName} numberOfLines={1}>
                                    {contact.name}
                                </Text>
                                {contact.isPrimary ? (
                                    <View style={styles.primaryBadge}>
                                        <Text style={styles.primaryBadgeText}>Primary</Text>
                                    </View>
                                ) : null}
                            </View>
                            <Text style={styles.contactLabel}>{contact.label}</Text>
                        </View>
                        <Pressable
                            onPress={() => handleCall(contact)}
                            disabled={!contact.callable || pendingRef !== null}
                            style={({ pressed }) => [
                                styles.callButton,
                                !contact.callable && styles.callDisabled,
                                pressed && styles.callPressed
                            ]}
                        >
                            {isPending ? (
                                <ActivityIndicator size="small" color="#FFFFFF" />
                            ) : (
                                <MaterialCommunityIcons name="phone" size={18} color="#FFFFFF" />
                            )}
                        </Pressable>
                    </View>
                );
            })}
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1.5,
        borderColor: colors.border,
        padding: spacing.base,
        ...shadows.sm
    },
    sectionLabel: {
        fontFamily: fonts.bold,
        fontSize: 10,
        color: colors.teal500,
        letterSpacing: 1,
        textTransform: "uppercase",
        marginBottom: spacing.md
    },
    row: {
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: spacing.md
    },
    rowBorder: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    contactInfo: {
        flex: 1
    },
    nameRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        flex: 1
    },
    contactName: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textPrimary,
        flexShrink: 1
    },
    primaryBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: spacing.sm,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    primaryBadgeText: {
        fontFamily: fonts.bold,
        fontSize: 9,
        color: colors.teal500,
        textTransform: "uppercase"
    },
    contactLabel: {
        fontFamily: fonts.medium,
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 2
    },
    callButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.teal400,
        alignItems: "center",
        justifyContent: "center"
    },
    callPressed: {
        backgroundColor: colors.teal500
    },
    callDisabled: {
        opacity: 0.4
    },
    emptyText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textTertiary,
        fontStyle: "italic"
    }
});
